import React, { useState, useEffect, useCallback } from "react";
import {
    Backdrop, CircularProgress, Box, Dialog, DialogTitle,
    DialogContent, DialogActions, Button, TextField, Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import HSTable from "../../components/global/Table";
import Notify from "../../components/global/Notify";
import TableActionButton from "../../components/global/TableActionButton";
import { getAllPricesPerKg, updatePricePerKg, translatePricePerKgType } from "../../services/prices/PricePerKgService";

const formatDate = (iso) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleString("pt-PT");
};

const PricePerKgPage = () => {
    const [rows, setRows] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [editRow, setEditRow] = useState(null);
    const [newPrice, setNewPrice] = useState("");
    const [saving, setSaving] = useState(false);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getAllPricesPerKg(0, 50, "");
            setRows(res.content);
            setTotalCount(res.totalElements);
        } catch (error) {
            if (error?.silent) return;
            Notify.error(error.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleEdit = (row) => {
        setEditRow(row);
        setNewPrice(String(row.pricePerKg));
    };

    const handleSave = async () => {
        const parsed = parseFloat(newPrice);
        if (isNaN(parsed) || parsed <= 0) {
            Notify.error("Introduza um valor válido.");
            return;
        }
        setSaving(true);
        try {
            await updatePricePerKg(editRow.id, { ...editRow, pricePerKg: parsed });
            Notify.success("Preço atualizado com sucesso.");
            setEditRow(null);
            fetchData();
        } catch (error) {
            Notify.error(error.message);
        } finally {
            setSaving(false);
        }
    };

    const COLUMNS = [
        { field: "type", label: "Tipo", sortable: true, render: (v) => translatePricePerKgType(v) },
        {
            field: "pricePerKg", label: "Preço/Kg (€)", align: "right", sortable: true,
            render: (v) => `${v?.toFixed(2)} €`,
        },
        { field: "updatedAt", label: "Última Atualização", render: (v) => formatDate(v) },
        {
            field: "id", label: "Ações", align: "center",
            render: (_, row) => (
                <TableActionButton label="Editar preço" onClick={(e) => { e.stopPropagation(); handleEdit(row); }}>
                    <EditIcon fontSize="small" />
                </TableActionButton>
            ),
        },
    ];

    return (
        <>
            <Box sx={{ position: "relative", width: "100%" }}>
                <HSTable
                    columns={COLUMNS}
                    data={rows}
                    loading={loading}
                    totalCount={totalCount}
                    rowsPerPageOptions={[10, 25, 50]}
                    enableSearch={false}
                />

                <Backdrop
                    open={loading}
                    sx={{
                        position: "absolute",
                        backgroundColor: "rgba(255, 255, 255, 0.8)",
                        color: "#04642c",
                        zIndex: 1,
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                    }}
                >
                    <CircularProgress color="inherit" size={40} />
                </Backdrop>
            </Box>

            <Dialog open={!!editRow} onClose={() => setEditRow(null)} maxWidth="xs" fullWidth>
                <DialogTitle>Editar Preço/Kg</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        Tipo: <strong>{translatePricePerKgType(editRow?.type)}</strong>
                    </Typography>
                    <TextField
                        label="Preço por Kg (€)"
                        type="number"
                        fullWidth
                        value={newPrice}
                        onChange={(e) => setNewPrice(e.target.value)}
                        inputProps={{ min: 0, step: 0.1 }}
                        autoFocus
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditRow(null)} disabled={saving}>Cancelar</Button>
                    <Button onClick={handleSave} variant="contained" disabled={saving}>
                        {saving ? <CircularProgress size={18} /> : "Guardar"}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default PricePerKgPage;
